import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { formatSimpleRelationLabel } from '../utils/familyTreeUtils';
import { PublishedFamilyTreeRecord } from '../types/familyTree';
import {
  CORE_CONDITION_OPTIONS,
  FamilyHistoryChangeRequestRecord,
  IntakeConditionCode,
  IntakeContext,
  IntakeFamilyConditionAssignment,
  IntakeFamilyStructure,
  IntakeLifestyle,
  IntakeMedicalTimelineEntry,
  IntakePersonalAllergy,
  IntakePersonalDiagnosis,
  IntakePersonalHistory,
  IntakePersonalInfo,
  IntakePersonalMedication,
  IntakePriorGeneticTest,
  IntakeRelativeRecord,
  IntakeVaccinationHistory,
  IntakeWorkflowStatus,
  PatientFamilyHistoryIntakeRecord,
} from '../types/familyHistoryIntake';

const EMPTY_ADDRESS = {
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
};

const EMPTY_PERSONAL_INFO: IntakePersonalInfo = {
  displayName: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  dateOfBirth: '',
  sexAtBirth: '',
  address: EMPTY_ADDRESS,
};

const EMPTY_CONTEXT: IntakeContext = {
  occupation: '',
  educationLevel: '',
  householdContext: '',
  financialConstraints: '',
  supportNetworkNotes: '',
};

const EMPTY_LIFESTYLE: IntakeLifestyle = {
  smokingStatus: '',
  alcoholUse: '',
  drinkFrequency: '',
  drinksPerSession: '',
  heavySessionFrequency: '',
  drinkType: '',
  drugUse: '',
  drugDetail: '',
  exercisePattern: '',
  sleepPattern: '',
  dietPattern: '',
  stressLevel: '',
  exposuresNotes: '',
};

const EMPTY_VACCINATIONS: IntakeVaccinationHistory = {
  childhoodVaccinationsUpToDate: 'unknown',
  covidVaccinationStatus: '',
  travelVaccinationsNotes: '',
  otherVaccinationNotes: '',
};

const EMPTY_PERSONAL_HISTORY: IntakePersonalHistory = {
  medications: [],
  allergies: [],
  activeDiagnoses: [],
  priorGeneticTests: [],
  ancestries: [],
  ancestryNotes: '',
};

const EMPTY_FAMILY_STRUCTURE: IntakeFamilyStructure = {
  siblingsCount: null,
  siblingsHaveChildren: 'unknown',
  maternalAuntsCount: null,
  maternalUnclesCount: null,
  paternalAuntsCount: null,
  paternalUnclesCount: null,
  knewAllGrandparents: 'unknown',
  childrenCount: null,
  notes: '',
};

export const canPatientEditIntake = (status: IntakeWorkflowStatus) =>
  status === 'draft' || status === 'changes_requested';

export const isPatientIntakeLocked = (status: IntakeWorkflowStatus) =>
  !canPatientEditIntake(status);

export function createEmptyTimelineEntry(): IntakeMedicalTimelineEntry {
  return {
    id: crypto.randomUUID(),
    label: '',
    dateLabel: '',
    notes: '',
  };
}

export function createEmptyConditionAssignment(
  conditionKey: string,
  conditionLabel: string,
  code?: IntakeConditionCode,
): IntakeFamilyConditionAssignment {
  return {
    id: crypto.randomUUID(),
    conditionKey,
    conditionLabel,
    code,
    response: 'neither',
    familyMemberIds: [],
    selfNotes: '',
    familyNotes: '',
    relativeDetails: [],
  };
}

export function createEmptyMedication(): IntakePersonalMedication {
  return {
    id: crypto.randomUUID(),
    name: '',
    doseAmount: null,
    doseUnit: 'mg',
    doseLabel: '',
    frequencyTimes: null,
    frequencyPeriod: 'day',
    frequencyLabel: '',
    reason: '',
  };
}

export function createEmptyAllergy(): IntakePersonalAllergy {
  return { id: crypto.randomUUID(), allergen: '', reactionLabel: '', severity: 'unknown' };
}

export function createEmptyDiagnosis(): IntakePersonalDiagnosis {
  return { id: crypto.randomUUID(), label: '', diagnosedYear: null, ageAtDiagnosis: null, notes: '' };
}

export function createEmptyGeneticTest(): IntakePriorGeneticTest {
  return { id: crypto.randomUUID(), testName: '', yearLabel: '', providerLabel: '', resultSummary: '', knownVariants: '' };
}

export function ensurePersonalHistory(intake: PatientFamilyHistoryIntakeRecord): IntakePersonalHistory {
  return {
    medications: intake.personalHistory?.medications ?? [],
    allergies: intake.personalHistory?.allergies ?? [],
    activeDiagnoses: intake.personalHistory?.activeDiagnoses ?? [],
    priorGeneticTests: intake.personalHistory?.priorGeneticTests ?? [],
    ancestries: intake.personalHistory?.ancestries ?? [],
    ancestryNotes: intake.personalHistory?.ancestryNotes ?? '',
  };
}

export function createEmptyRelative(overrides: Partial<IntakeRelativeRecord> = {}): IntakeRelativeRecord {
  return {
    id: crypto.randomUUID(),
    relation: 'mother',
    relationLabel: '',
    relationOtherDetails: '',
    lineageSide: 'maternal',
    bloodRelated: true,
    connectedToRelativeId: '',
    sex: 'unknown',
    aliveStatus: 'alive',
    currentAge: null,
    ageAtDeath: null,
    causeOfDeath: '',
    priorGeneticTesting: 'unknown',
    conditions: [],
    notes: '',
    ...overrides,
  };
}

export function createDefaultFamilyHistoryIntake(userId: string, seed?: Partial<IntakePersonalInfo>): PatientFamilyHistoryIntakeRecord {
  // If the sign-up provided a `displayName` but no first/last, split on the
  // first whitespace so the patient doesn't have to re-enter their name.
  const derivedFirst = seed?.firstName || (seed?.displayName?.trim().split(/\s+/)?.[0] ?? '');
  const derivedLast = seed?.lastName || (seed?.displayName?.trim().split(/\s+/)?.slice(1).join(' ') ?? '');

  return {
    userId,
    status: 'draft',
    currentVersion: 1,
    personalInfo: {
      ...EMPTY_PERSONAL_INFO,
      ...seed,
      firstName: derivedFirst,
      lastName: derivedLast,
      address: {
        ...EMPTY_ADDRESS,
        ...(seed?.address || {}),
      },
    },
    context: EMPTY_CONTEXT,
    lifestyle: EMPTY_LIFESTYLE,
    vaccinationHistory: EMPTY_VACCINATIONS,
    personalHistory: { ...EMPTY_PERSONAL_HISTORY },
    surgeries: [],
    hospitalisations: [],
    traumaHistory: [],
    familyStructure: EMPTY_FAMILY_STRUCTURE,
    relatives: [],
    conditionAssignments: CORE_CONDITION_OPTIONS.map((condition) =>
      createEmptyConditionAssignment(condition.key, condition.label, condition.code),
    ),
    patternFlags: [],
    patientNotes: '',
    opsClarificationMessage: '',
    source: 'patient',
  };
}

function buildRelationLabel(relative: IntakeRelativeRecord) {
  if (relative.relation === 'other' && relative.relationOtherDetails?.trim()) return relative.relationOtherDetails.trim();
  return formatSimpleRelationLabel(relative.relation);
}

function getRelativeBranch(relative: IntakeRelativeRecord) {
  const relation = relative.relation;
  if (relation === 'child' || relation === 'son' || relation === 'daughter') return 'children' as const;
  if (['brother', 'sister', 'half_brother', 'half_sister'].includes(relation)) return 'siblings' as const;
  if (relation === 'self') return 'self' as const;
  if (relative.lineageSide === 'maternal') return 'maternal' as const;
  if (relative.lineageSide === 'paternal') return 'paternal' as const;
  return 'shared' as const;
}

function getRelativeGeneration(relative: IntakeRelativeRecord) {
  switch (relative.relation) {
    case 'maternal_grandmother':
    case 'maternal_grandfather':
    case 'paternal_grandmother':
    case 'paternal_grandfather':
    case 'grandmother':
    case 'grandfather':
      return 0;
    case 'mother':
    case 'father':
    case 'maternal_aunt':
    case 'maternal_uncle':
    case 'paternal_aunt':
    case 'paternal_uncle':
    case 'aunt':
    case 'uncle':
      return 1;
    case 'child':
    case 'son':
    case 'daughter':
      return 3;
    default:
      return 2;
  }
}

export function buildFamilyTreePreviewFromIntake(intake: PatientFamilyHistoryIntakeRecord | null): PublishedFamilyTreeRecord | null {
  if (!intake) return null;

  const nodes = [
    {
      id: 'member-self',
      relationKey: 'self',
      relationLabel: intake.personalInfo.displayName || 'You',
      lineageSide: 'self',
      generation: 2,
      branch: 'self' as const,
      aliveStatus: 'alive' as const,
      healthSignal: intake.conditionAssignments.some((c) => c.response === 'self' || c.response === 'both')
        ? 'affected' as const
        : 'healthy' as const,
      caption: 'Your draft family history map',
      notes: intake.patientNotes || undefined,
      conditions: intake.conditionAssignments
        .filter((condition) => condition.response === 'self' || condition.response === 'both')
        .map((condition) => ({
          label: condition.conditionLabel,
          category: 'self',
        })),
    },
    ...intake.relatives.map((relative) => ({
      id: relative.id,
      relationKey: relative.relation,
      relationLabel: buildRelationLabel(relative),
      connectedToNodeId: relative.connectedToRelativeId || undefined,
      lineageSide: relative.lineageSide,
      generation: getRelativeGeneration(relative),
      branch: getRelativeBranch(relative),
      aliveStatus: relative.aliveStatus,
      healthSignal: relative.conditions.length ? 'affected' as const : 'healthy' as const,
      caption: relative.conditions.length ? relative.conditions.map((condition) => condition.label).join(', ') : 'No concerns noted',
      notes: relative.notes,
      conditions: relative.conditions.map((condition) => ({
        label: condition.label,
        category: 'family',
        ageAtDiagnosis: condition.ageAtDiagnosis ?? null,
      })),
    })),
  ];

  const edges = intake.relatives.map((relative) => {
    let to = 'member-self';
    const relation = relative.relation;
    const isCousin = relation === 'cousin' || relation === 'maternal_cousin' || relation === 'paternal_cousin';
    const isMaternalGrandparent = relation === 'maternal_grandmother' || relation === 'maternal_grandfather'
      || ((relation === 'grandmother' || relation === 'grandfather') && relative.lineageSide === 'maternal');
    const isPaternalGrandparent = relation === 'paternal_grandmother' || relation === 'paternal_grandfather'
      || ((relation === 'grandmother' || relation === 'grandfather') && relative.lineageSide === 'paternal');

    if (isCousin && relative.connectedToRelativeId) {
      to = relative.connectedToRelativeId;
    } else if (isMaternalGrandparent) {
      to = intake.relatives.find((candidate) => {
        const rel = candidate.relation;
        return rel === 'mother' || rel === 'maternal_aunt' || rel === 'maternal_uncle'
          || (candidate.lineageSide === 'maternal' && ['aunt', 'uncle'].includes(rel));
      })?.id || 'member-self';
    } else if (isPaternalGrandparent) {
      to = intake.relatives.find((candidate) => {
        const rel = candidate.relation;
        return rel === 'father' || rel === 'paternal_aunt' || rel === 'paternal_uncle'
          || (candidate.lineageSide === 'paternal' && ['aunt', 'uncle'].includes(rel));
      })?.id || 'member-self';
    }

    return { from: relative.id, to };
  });

  return {
    userId: intake.userId,
    workflowId: null,
    sourceInterviewId: null,
    status: 'locked',
    publicationStatus: 'not_ready',
    teaserVersion: 'intake-preview',
    summaryNarrative: 'This preview shows the family tree draft you have submitted for clinical review.',
    memberMessage: '',
    nodes,
    edges,
    insightChips: [],
  };
}

export function normalizeLegacyUserIntoIntake(input: {
  userId: string;
  userEmail: string;
  userDisplayName: string;
  legacy: Record<string, any> | null;
}): PatientFamilyHistoryIntakeRecord {
  const { userId, userEmail, userDisplayName, legacy } = input;
  const base = createDefaultFamilyHistoryIntake(userId, {
    displayName: legacy?.displayName || userDisplayName || '',
    firstName: legacy?.firstName || '',
    lastName: legacy?.lastName || '',
    email: legacy?.email || userEmail || '',
    phone: legacy?.phone || '',
    dateOfBirth: legacy?.dateOfBirth || '',
  });

  return {
    ...base,
    personalInfo: {
      ...base.personalInfo,
      address: {
        ...base.personalInfo.address,
        line1: legacy?.shippingAddress?.line1 || '',
        line2: legacy?.shippingAddress?.line2 || '',
        city: legacy?.shippingAddress?.city || '',
        postcode: legacy?.shippingAddress?.postcode || '',
        country: legacy?.shippingAddress?.country || 'United Kingdom',
      },
    },
    lifestyle: {
      ...base.lifestyle,
      dietPattern: legacy?.dietPattern || '',
      exposuresNotes: legacy?.familyHistorySummary || '',
    },
    patientNotes: [
      legacy?.familyHistorySummary,
      legacy?.maternalHistory,
      legacy?.paternalHistory,
      legacy?.siblingsHistory,
      legacy?.childrenHistory,
      legacy?.healthGoals,
    ]
      .filter(Boolean)
      .join('\n\n'),
    source: legacy ? 'legacy_migration' : 'patient',
  };
}

/**
 * Fills in every required sub-object the declared type promises but Firestore
 * does not actually hold. Mutates and returns the same record.
 *
 * Shape-normalisation runs on EVERY read, regardless of status. Records written
 * by the ops/doctor portals, Cloud Functions and seeds are routinely missing
 * whole branches, and the intake screens dereference those without guarding:
 * `intake.lifestyle.smokingStatus`, `intake.familyStructure.siblingsCount`,
 * `intake.conditionAssignments.filter(...)`. Any one of those throws on an
 * undefined parent and the error boundary eats the whole page.
 *
 * This used to repair `personalInfo` only, which is how it kept happening. A
 * survey of the staging collection found 15 of 17 records missing `context` and
 * `familyStructure`, 14 missing `personalHistory`, 6 missing `lifestyle`, and 2
 * — both investor-demo accounts — missing `conditionAssignments`. That is not a
 * legacy tail to migrate around; it is the normal shape of the data, so the
 * read path has to absorb it. The declared type gives no warning here because
 * the type is simply lying about what Firestore holds.
 *
 * Deliberately NOT a Firestore write. A member opening a page must not rewrite
 * a released, clinician-signed-off record as a side effect; the defaults exist
 * to render, and only a real edit should persist them.
 */
export function normalizeIntakeShape(intake: PatientFamilyHistoryIntakeRecord): PatientFamilyHistoryIntakeRecord {
  intake.personalInfo = {
    ...EMPTY_PERSONAL_INFO,
    ...(intake.personalInfo || {}),
    address: { ...EMPTY_ADDRESS, ...(intake.personalInfo?.address || {}) },
  };
  intake.context = { ...EMPTY_CONTEXT, ...(intake.context || {}) };
  intake.lifestyle = { ...EMPTY_LIFESTYLE, ...(intake.lifestyle || {}) };
  intake.vaccinationHistory = { ...EMPTY_VACCINATIONS, ...(intake.vaccinationHistory || {}) };
  intake.personalHistory = ensurePersonalHistory(intake);
  intake.familyStructure = { ...EMPTY_FAMILY_STRUCTURE, ...(intake.familyStructure || {}) };
  intake.surgeries = intake.surgeries || [];
  intake.hospitalisations = intake.hospitalisations || [];
  intake.traumaHistory = intake.traumaHistory || [];
  intake.relatives = intake.relatives || [];
  intake.patternFlags = intake.patternFlags || [];

  // The core conditions are the questionnaire itself, not saved answers. An
  // empty array is a blank set of questions the member can never answer, so
  // seed it the way a new intake would be.
  intake.conditionAssignments = intake.conditionAssignments?.length
    ? intake.conditionAssignments
    : CORE_CONDITION_OPTIONS.map((condition) =>
        createEmptyConditionAssignment(condition.key, condition.label, condition.code),
      );

  return intake;
}

export async function getFamilyHistoryIntake(userId: string, fallbackSeed?: { email?: string | null; displayName?: string | null }) {
  const intakeRef = doc(db, 'patient_family_history_intakes', userId);
  const [intakeSnap, userSnap] = await Promise.all([
    getDoc(intakeRef),
    getDoc(doc(db, 'users', userId)),
  ]);

  if (intakeSnap.exists()) {
    const intake = { id: intakeSnap.id, ...intakeSnap.data() } as PatientFamilyHistoryIntakeRecord;

    normalizeIntakeShape(intake);

    // If the patient is still editing and some basics are empty, backfill
    // from their signed-up `users/{uid}` profile so they don't see blank
    // fields on screens we've since started capturing at sign-up.
    if (userSnap.exists() && canPatientEditIntake(intake.status)) {
      const profile = userSnap.data() as Record<string, any>;
      const pi = intake.personalInfo;
      const addr = pi.address;
      const profileAddr = profile.shippingAddress || {};
      intake.personalInfo = {
        ...pi,
        firstName: pi.firstName || profile.firstName || '',
        lastName: pi.lastName || profile.lastName || '',
        displayName: pi.displayName || profile.displayName || '',
        email: pi.email || profile.email || '',
        phone: pi.phone || profile.phone || '',
        address: {
          ...addr,
          line1: addr.line1 || profileAddr.line1 || '',
          line2: addr.line2 || profileAddr.line2 || '',
          city: addr.city || profileAddr.city || '',
          postcode: addr.postcode || profileAddr.postcode || '',
          country: addr.country || profileAddr.country || 'United Kingdom',
        },
      };
    }
    return intake;
  }

  const legacy = userSnap.exists() ? userSnap.data() : null;
  return normalizeLegacyUserIntoIntake({
    userId,
    userEmail: legacy?.email || fallbackSeed?.email || '',
    userDisplayName: legacy?.displayName || fallbackSeed?.displayName || '',
    legacy,
  });
}

export async function saveFamilyHistoryIntakeDraft(intake: PatientFamilyHistoryIntakeRecord) {
  const intakeRef = doc(db, 'patient_family_history_intakes', intake.userId);
  await setDoc(intakeRef, {
    ...intake,
    source: intake.source || 'patient',
    lastEditableAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAt: intake.createdAt || serverTimestamp(),
  }, { merge: true });
}

export async function submitFamilyHistoryIntake(intake: PatientFamilyHistoryIntakeRecord) {
  const intakeRef = doc(db, 'patient_family_history_intakes', intake.userId);
  await setDoc(intakeRef, {
    ...intake,
    status: 'submitted',
    currentVersion: (intake.currentVersion || 1) + 1,
    source: intake.source || 'patient',
    latestSubmittedAt: serverTimestamp(),
    firstSubmittedAt: intake.firstSubmittedAt || serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdAt: intake.createdAt || serverTimestamp(),
  }, { merge: true });
}

export async function getFamilyHistoryChangeRequests(userId: string) {
  const snap = await getDocs(query(
    collection(db, 'family_history_change_requests'),
    where('userId', '==', userId),
    limit(20),
  ));

  return snap.docs
    .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }) as FamilyHistoryChangeRequestRecord)
    .sort((left, right) => (right.createdAt?.toMillis?.() || 0) - (left.createdAt?.toMillis?.() || 0));
}

export async function createFamilyHistoryChangeRequest(input: {
  userId: string;
  intakeId?: string | null;
  workflowId?: string | null;
  familyTreeId?: string | null;
  requestedArea: FamilyHistoryChangeRequestRecord['requestedArea'];
  message: string;
  proposedCorrection?: string;
}) {
  await addDoc(collection(db, 'family_history_change_requests'), {
    ...input,
    status: 'open',
    submittedAt: serverTimestamp(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}
